/**
 * Class Details Page
 * View detailed information about a class including enrolled students and lab infrastructure
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useClassDetails } from '../hooks/useClassDetails';
import { useClasses } from '../hooks/useClasses';
import { useLabs } from '../hooks/useLabs';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { EditClassDialog } from '../components/instructor/EditClassDialog';
import { DeleteClassDialog } from '../components/instructor/DeleteClassDialog';
import { ClassStatusDropdown } from '../components/instructor/ClassStatusDropdown';
import { LabStatusPanel } from '../components/instructor/LabStatusPanel';
import { apiGet, apiPut, apiDelete } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { Pencil, Trash2, UserMinus } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import type { ClassStatus, UpdateClassRequest } from '@cloak-next/shared';

/**
 * Format date for display
 * Returns formatted date or 'Invalid date' if the date string is invalid
 */
function formatDate(dateString: string): string {
  if (!dateString) {
    return 'Not set';
  }

  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function ClassDetailsPage() {
  const { className } = useParams<{ className: string }>();
  const navigate = useNavigate();
  const { classData, enrollments, loading, error, fetchClassDetails, refetch } = useClassDetails(className || '');
  const { updateClass, deleteClass } = useClasses();
  const { user } = useAuth();
  const userRole = user?.role ?? 'instructor';
  const {
    labStatus,
    fetchLabStatus,
    primeLabs,
    terminateLabs,
    reduceInstances,
    loading: labsLoading,
  } = useLabs();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [guidance, setGuidance] = useState('');
  const [guidanceSaving, setGuidanceSaving] = useState(false);
  const [guidanceLoaded, setGuidanceLoaded] = useState(false);
  const [pendingUnenroll, setPendingUnenroll] = useState<string | null>(null);
  const [isUnenrolling, setIsUnenrolling] = useState(false);
  const [unenrollError, setUnenrollError] = useState<string | null>(null);

  // Fetch class details on mount
  useEffect(() => {
    if (className) {
      fetchClassDetails(className);
    }
  }, [className, fetchClassDetails]);

  // Fetch lab status when class data is available and class is not draft
  useEffect(() => {
    if (classData && classData.status !== 'draft') {
      fetchLabStatus(classData.className).catch(console.error);
    }
  }, [classData?.className, classData?.status, fetchLabStatus]);

  // Load instructor guidance when class data is available
  useEffect(() => {
    if (classData && !guidanceLoaded) {
      apiGet<{ success: boolean; data: { markdown: string | null } }>(
        `/classes/${encodeURIComponent(classData.className)}/guidance`
      )
        .then(r => {
          setGuidance(r.data?.markdown || '');
          setGuidanceLoaded(true);
        })
        .catch(() => setGuidanceLoaded(true));
    }
  }, [classData?.className, guidanceLoaded]);

  const handleSaveGuidance = async () => {
    if (!classData) return;
    setGuidanceSaving(true);
    try {
      await apiPut(`/classes/${encodeURIComponent(classData.className)}/guidance`, { markdown: guidance });
    } finally {
      setGuidanceSaving(false);
    }
  };

  const handleUnenroll = async (studentEmail: string) => {
    setIsUnenrolling(true);
    setUnenrollError(null);
    try {
      await apiDelete(`/classes/${encodeURIComponent(className!)}/students/${encodeURIComponent(studentEmail)}`);
      setPendingUnenroll(null);
      refetch();
    } catch (err) {
      setUnenrollError(err instanceof Error ? err.message : 'Failed to unenroll student');
    } finally {
      setIsUnenrolling(false);
    }
  };

  const handleBack = () => {
    navigate('/classes');
  };

  const handlePrimeLabs = async (count: number) => {
    if (!classData) return;
    try {
      await primeLabs(classData.className, count);
      await refetch();
    } catch (err) {
      console.error('Failed to provision labs:', err);
      throw err; // Let dialog handle error display
    }
  };

  const handleTerminateLabs = async () => {
    if (!classData) return;
    try {
      await terminateLabs(classData.className);
      await refetch();
      await fetchLabStatus(classData.className);
    } catch (err) {
      console.error('Failed to terminate labs:', err);
      throw err;
    }
  };

  const handleReduceInstances = async (count: number) => {
    if (!classData) return;
    try {
      await reduceInstances(classData.className, count);
    } catch (err) {
      console.error('Failed to reduce instances:', err);
      throw err;
    }
  };

  const handleRefreshLabStatus = () => {
    if (classData) {
      fetchLabStatus(classData.className).catch(console.error);
    }
  };

  const handleStatusChange = async (newStatus: ClassStatus) => {
    if (!classData) return;
    try {
      setIsUpdating(true);
      await updateClass(classData.className, { status: newStatus });
      await refetch();
    } catch (err) {
      console.error('Failed to change class status:', err);
      // TODO: Show error toast
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateClass = async (className: string, updates: UpdateClassRequest) => {
    await updateClass(className, updates);
    await refetch();
  };

  const handleDeleteClass = async (className: string) => {
    await deleteClass(className);
    // Navigate back to classes list after successful deletion
    navigate('/classes');
  };

  const handleTerminateLabsForDelete = async () => {
    if (!classData) return;
    await terminateLabs(classData.className);
    await refetch();
    await fetchLabStatus(classData.className);
  };

  const copyClassCode = () => {
    if (classData) {
      navigator.clipboard.writeText(classData.classCode);
      // TODO: Add toast notification
    }
  };

  // Loading state
  if (loading && !classData) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={handleBack}>
          ← Back to Classes
        </Button>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading class details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !classData) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={handleBack}>
          ← Back to Classes
        </Button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          <p className="font-semibold">Error loading class</p>
          <p className="text-sm">{error || 'Class not found'}</p>
        </div>
      </div>
    );
  }

  const showLabPanel = classData.status !== 'draft';

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handleBack}>
          ← Back to Classes
        </Button>
        <div className="flex gap-2">
          {classData.status !== 'ended' && (
            <Button variant="outline" onClick={() => setIsEditDialogOpen(true)} disabled={loading || isUpdating}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          <Button variant="outline" onClick={() => setIsDeleteDialogOpen(true)} disabled={loading || isUpdating}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <ClassStatusDropdown
            classData={classData}
            onStatusChange={handleStatusChange}
            disabled={loading || isUpdating}
          />
        </div>
      </div>

      {/* Class Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">
                {classData.classTitle || classData.className}
              </h2>
              {classData.classTitle && (
                <p className="text-sm text-muted-foreground font-mono">
                  {classData.className}
                </p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Enrollment Code:{' '}
                  <button
                    onClick={copyClassCode}
                    className="font-mono hover:underline font-medium"
                    title="Click to copy"
                  >
                    {classData.classCode}
                  </button>
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Instructor:</span>
              <p className="font-medium">{classData.instructorEmail}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Students Enrolled:</span>
              <p className="font-medium">
                {classData.currentStudents} / {classData.maxStudents}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Start Time:</span>
              <p className="font-medium">{formatDate(classData.startTime)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">End Time:</span>
              <p className="font-medium">{formatDate(classData.endTime)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Environment:</span>
              <p className="font-medium">{classData.environmentId || 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <p className="font-medium capitalize">{classData.status}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lab Status Panel (only for active classes) */}
      {showLabPanel && (
        <LabStatusPanel
          classData={classData}
          labStatus={labStatus}
          currentEnrollments={enrollments.length}
          loading={labsLoading}
          onPrime={handlePrimeLabs}
          onReduce={handleReduceInstances}
          onTerminateAll={handleTerminateLabs}
          onRefresh={handleRefreshLabStatus}
        />
      )}

      {/* Getting Started Instructions Editor */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold">Getting Started Instructions</h3>
          <p className="text-sm text-muted-foreground">
            Markdown shown to students when they open Getting Started. Copied from the environment template at class creation — edit freely.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="write">
            <TabsList>
              <TabsTrigger value="write">Write</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            <TabsContent value="write">
              <textarea
                className="w-full min-h-[200px] font-mono text-sm border rounded p-3 resize-y"
                placeholder="# Getting Started Instructions..."
                value={guidance}
                onChange={e => setGuidance(e.target.value)}
              />
            </TabsContent>
            <TabsContent value="preview">
              {guidance ? (
                <div className="prose prose-sm max-w-none border rounded p-4">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{guidance}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic p-4">Nothing to preview yet.</p>
              )}
            </TabsContent>
          </Tabs>
          <div className="mt-3 flex justify-end">
            <Button onClick={handleSaveGuidance} disabled={guidanceSaving}>
              {guidanceSaving ? 'Saving...' : 'Save Instructions'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enrolled Students */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold">Enrolled Students</h3>
          <p className="text-sm text-muted-foreground">
            {enrollments.length} student{enrollments.length !== 1 ? 's' : ''} enrolled
          </p>
        </CardHeader>
        <CardContent>
          {enrollments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No students enrolled yet. Share the class code <span className="font-mono font-medium">{classData.classCode}</span> to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-2 font-medium">Student Email</th>
                    <th className="pb-2 font-medium">Instance ID</th>
                    <th className="pb-2 font-medium">Enrolled</th>
                    <th className="pb-2 font-medium">Last Access</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((enrollment) => (
                    <tr key={enrollment.studentEmail} className="border-b last:border-0">
                      <td className="py-3">{enrollment.studentEmail}</td>
                      <td className="py-3">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                          {enrollment.instanceId || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {formatDistanceToNow(new Date(enrollment.enrolledAt), { addSuffix: true })}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {enrollment.lastAccess
                          ? formatDistanceToNow(new Date(enrollment.lastAccess), { addSuffix: true })
                          : 'Never'}
                      </td>
                      <td className="py-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setPendingUnenroll(enrollment.studentEmail)}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unenroll Student Confirmation */}
      <AlertDialog open={pendingUnenroll !== null} onOpenChange={(open) => !open && setPendingUnenroll(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unenroll student?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <span className="font-medium">{pendingUnenroll}</span> from this class.
              If they have an assigned lab instance, it will be terminated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {unenrollError && <p className="text-sm text-destructive px-1">{unenrollError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnenrolling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => pendingUnenroll && handleUnenroll(pendingUnenroll)}
              disabled={isUnenrolling}
            >
              {isUnenrolling ? 'Unenrolling...' : 'Unenroll'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Class Dialog */}
      <EditClassDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        classData={classData}
        onUpdateClass={handleUpdateClass}
        userRole={userRole}
      />

      {/* Delete Class Dialog */}
      <DeleteClassDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        classData={classData}
        onDeleteClass={handleDeleteClass}
        onTerminateLabs={handleTerminateLabsForDelete}
      />
    </div>
  );
}
